import React, { useEffect, useState } from 'react';
import relatorioService, {
  type PrestacaoExecucaoFisicaMeta,
  type PrestacaoPcFinanceiro,
} from '../../services/relatorioPrestacaoContasService';
import DownloadButtons from './DownloadButtons';
import { Table, Tbody, Td, Th, Thead, Tr } from '../../components/ui/Table';
import {
  bodyTextClass, formatCurrency, labelTextClass, strongTextClass, subtleSurfaceClass,
  surfaceClass, type SectionProps,
} from './shared';

const Anexo2ExecucaoFisicaSection: React.FC<SectionProps> = ({ pcId }) => {
  const [execucoes, setExecucoes] = useState<PrestacaoExecucaoFisicaMeta[]>([]);
  const [financeiro, setFinanceiro] = useState<PrestacaoPcFinanceiro | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const carregar = async () => {
    setLoading(true);
    setError(null);
    try {
      const [exec, fin] = await Promise.all([
        relatorioService.getExecucaoFisica(pcId),
        relatorioService.getFinanceiro(pcId),
      ]);
      setExecucoes(exec);
      setFinanceiro(fin);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar a execução física.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className={`text-lg font-semibold ${strongTextClass}`}>Anexo 2 · Execução Físico-Financeira</h2>
        <DownloadButtons pcId={pcId} anexo="2" nomeArquivo="anexo-2-execucao-fisico-financeira" />
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : (
        <>
          <div className={`overflow-hidden rounded-2xl border ${surfaceClass}`}>
            <Table density="compact">
              <Thead className={subtleSurfaceClass}>
                <Tr>
                  <Th className={labelTextClass}>Meta</Th>
                  <Th align="right" className={labelTextClass}>Valor total</Th>
                  <Th align="right" className={labelTextClass}>Executado no período</Th>
                  <Th align="right" className={labelTextClass}>Executado até o período</Th>
                </Tr>
              </Thead>
              <Tbody>
                {execucoes.length === 0 && (
                  <Tr><Td align="center" colSpan={4} className={labelTextClass}>Nenhuma meta cadastrada no plano de trabalho.</Td></Tr>
                )}
                {execucoes.map((e) => (
                  <Tr key={e.metaId} className="border-t border-slate-200 dark:border-slate-800">
                    <Td className={strongTextClass}>{e.metaTitulo}</Td>
                    <Td align="right">{formatCurrency(e.valorMetaPlanejado)}</Td>
                    <Td align="right">
                      <span className={bodyTextClass}>{formatCurrency(e.quantidadeExecutada)}</span>
                    </Td>
                    <Td align="right">{formatCurrency(e.executadoAtePeriodo)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          <div className={`rounded-2xl border p-4 ${subtleSurfaceClass}`}>
            <p className={`mb-3 text-xs font-semibold uppercase tracking-[0.16em] ${labelTextClass}`}>Execução financeira desta PC</p>
            <p className={`mb-3 text-xs ${labelTextClass}`}>
              Detalhamento por item (etapa) e a tabela encadeada com todas as PCs ficam na aba "Metas e Etapas Executadas" / "Execução Financeira". Contrapartida e rendimento são informados junto dos comprovantes na aba "Execução Financeira".
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <p className={`text-xs ${labelTextClass}`}>Saldo anterior</p>
                <p className={strongTextClass}>{formatCurrency(financeiro?.saldoAnterior)}</p>
              </div>
              <div>
                <p className={`text-xs ${labelTextClass}`}>Valor recebido</p>
                <p className={strongTextClass}>{formatCurrency(financeiro?.valorRecebido)}</p>
              </div>
              <div>
                <p className={`text-xs ${labelTextClass}`}>Valor utilizado</p>
                <p className={strongTextClass}>{formatCurrency(financeiro?.valorUtilizado)}</p>
              </div>
              <div>
                <p className={`text-xs ${labelTextClass}`}>Contrapartida</p>
                <p className={strongTextClass}>{formatCurrency(financeiro?.contrapartida)}</p>
              </div>
              <div>
                <p className={`text-xs ${labelTextClass}`}>Rendimento de aplicação</p>
                <p className={strongTextClass}>{formatCurrency(financeiro?.rendimentoAplicacao)}</p>
              </div>
              <div>
                <p className={`text-xs ${labelTextClass}`}>Saldo</p>
                <p className={strongTextClass}>{formatCurrency(financeiro?.saldo)}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Anexo2ExecucaoFisicaSection;
